import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  HardDrive,
  Key,
  Folder,
  File,
  RefreshCw,
  Search,
  ExternalLink,
  Copy,
  Check,
  X,
  Upload,
  Film,
  Sparkles,
  AlertCircle
} from 'lucide-react';
import {
  StorageData,
  KvData,
  fetchStorageObjects,
  fetchKvKeys,
  fetchKvValue,
  getInspectAdminSecret
} from '../../services/inspectService';

export const StorageTab: React.FC = () => {
  const [activeSubTab, setActiveSubTab] = useState<'r2' | 'kv'>('r2');

  // R2 state
  const [r2Prefix, setR2Prefix] = useState<string>('clips/');
  const [r2Data, setR2Data] = useState<StorageData | null>(null);
  const [isLoadingR2, setIsLoadingR2] = useState(false);
  const isFetchingR2Ref = useRef(false);

  // Outro Video Management state (R2: assets/soulsync_outro_16_9.mp4)
  const [outroTimestamp, setOutroTimestamp] = useState<number>(Date.now());
  const [isUploadingOutro, setIsUploadingOutro] = useState<boolean>(false);
  const [outroUploadSuccess, setOutroUploadSuccess] = useState<string | null>(null);
  const [outroUploadError, setOutroUploadError] = useState<string | null>(null);
  const outroFileInputRef = useRef<HTMLInputElement | null>(null);

  // KV state
  const [kvData, setKvData] = useState<KvData | null>(null);
  const [isLoadingKv, setIsLoadingKv] = useState(false);
  const [selectedKvKey, setSelectedKvKey] = useState<string | null>(null);
  const [kvValueData, setKvValueData] = useState<any>(null);
  const [isLoadingKvVal, setIsLoadingKvVal] = useState(false);

  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Load R2 objects
  const loadR2 = useCallback(async (prefix: string) => {
    if (isFetchingR2Ref.current) return;
    isFetchingR2Ref.current = true;
    setIsLoadingR2(true);
    try {
      const data = await fetchStorageObjects(prefix, 50);
      if (data) setR2Data(data);
    } finally {
      setIsLoadingR2(false);
      isFetchingR2Ref.current = false;
    }
  }, []);

  // Load KV keys
  const loadKv = useCallback(async () => {
    setIsLoadingKv(true);
    try {
      const data = await fetchKvKeys('', 50);
      if (data) setKvData(data);
    } finally {
      setIsLoadingKv(false);
    }
  }, []);

  useEffect(() => {
    if (activeSubTab === 'r2') {
      loadR2(r2Prefix);
    } else {
      loadKv();
    }
  }, [activeSubTab, r2Prefix, loadR2, loadKv]);

  const handleInspectKvKey = async (keyName: string) => {
    setSelectedKvKey(keyName);
    setIsLoadingKvVal(true);
    try {
      const res = await fetchKvValue(keyName);
      if (res.success) {
        setKvValueData(res.value);
      } else {
        setKvValueData({ error: res.error });
      }
    } finally {
      setIsLoadingKvVal(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(text);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleOutroFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.mp4')) {
      setOutroUploadError('Only .mp4 video files are accepted for the outro bumper.');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      setOutroUploadError('Outro video file must be under 20MB.');
      return;
    }

    setIsUploadingOutro(true);
    setOutroUploadError(null);
    setOutroUploadSuccess(null);

    try {
      const secret = getInspectAdminSecret();
      const headers: Record<string, string> = {
        'Content-Type': 'video/mp4'
      };
      if (secret) {
        headers['X-Admin-Secret'] = secret;
      }

      const res = await fetch('/api/media/assets/soulsync_outro_16_9.mp4', {
        method: 'PUT',
        credentials: 'include',
        headers,
        body: file
      });

      if (!res.ok) {
        throw new Error(`Upload failed with status ${res.status}`);
      }

      setOutroTimestamp(Date.now());
      setOutroUploadSuccess(`Outro updated successfully in R2! (${(file.size / 1024).toFixed(1)} KB)`);
      setTimeout(() => setOutroUploadSuccess(null), 4000);
      loadR2('assets/');
    } catch (err: any) {
      setOutroUploadError(err.message || 'Failed to upload outro to R2');
    } finally {
      setIsUploadingOutro(false);
      if (outroFileInputRef.current) {
        outroFileInputRef.current.value = '';
      }
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div>
      {/* Subtab Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '1.25rem',
        padding: '0.75rem 1.25rem',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-card)',
        border: '1px solid var(--border-glass)',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <HardDrive size={18} color="var(--accent-coral)" />
          <span style={{ fontWeight: 700, color: '#fff', fontSize: '0.9rem' }}>Storage & Media Explorer</span>
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            onClick={() => setActiveSubTab('r2')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: activeSubTab === 'r2' ? 'var(--accent-purple)' : 'transparent',
              color: '#fff',
              fontSize: '0.8rem',
              fontWeight: activeSubTab === 'r2' ? 700 : 500,
              cursor: 'pointer'
            }}
          >
            <Folder size={14} />
            <span>R2 Bucket (soulsync-media)</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab('kv')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: activeSubTab === 'kv' ? 'var(--accent-purple)' : 'transparent',
              color: '#fff',
              fontSize: '0.8rem',
              fontWeight: activeSubTab === 'kv' ? 700 : 500,
              cursor: 'pointer'
            }}
          >
            <Key size={14} />
            <span>KV Namespace (SOULSYNC_CHALLENGES)</span>
          </button>
        </div>
      </div>

      {/* ────────────────────────────────────────────── */}
      {/* 1. R2 BUCKET EXPLORER                          */}
      {/* ────────────────────────────────────────────── */}
      {activeSubTab === 'r2' && (
        <div>
          {/* Signature Outro Video Manager Card */}
          <div
            style={{
              padding: '1.1rem 1.25rem',
              borderRadius: 'var(--radius-lg)',
              background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.12) 0%, rgba(30, 27, 75, 0.4) 100%)',
              border: '1px solid rgba(168, 85, 247, 0.35)',
              marginBottom: '1.25rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Film size={18} color="#c084fc" />
                <span style={{ fontWeight: 700, color: '#f3e8ff', fontSize: '0.92rem' }}>
                  Signature 3-Second Outro Bumper
                </span>
                <span
                  style={{
                    fontSize: '0.66rem',
                    padding: '1px 6px',
                    borderRadius: '9999px',
                    background: 'rgba(34, 197, 94, 0.2)',
                    border: '1px solid rgba(34, 197, 94, 0.4)',
                    color: '#86efac',
                    fontWeight: 700
                  }}
                >
                  Active in R2
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="file"
                  ref={outroFileInputRef}
                  onChange={handleOutroFileChange}
                  accept="video/mp4,video/*"
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  onClick={() => outroFileInputRef.current?.click()}
                  disabled={isUploadingOutro}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 14px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                    border: 'none',
                    color: '#fff',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    cursor: isUploadingOutro ? 'wait' : 'pointer',
                    boxShadow: '0 2px 10px rgba(168, 85, 247, 0.35)'
                  }}
                >
                  <Upload size={14} />
                  <span>{isUploadingOutro ? 'Uploading to R2...' : 'Upload New Outro MP4'}</span>
                </button>
              </div>
            </div>

            <p style={{ margin: 0, fontSize: '0.74rem', color: '#cbd5e1', lineHeight: 1.4 }}>
              This is the golden master video appended to all external exports (with dynamic creator credit overlay). Stored at <code style={{ color: '#e9d5ff', background: 'rgba(0,0,0,0.3)', padding: '1px 5px', borderRadius: '4px' }}>assets/soulsync_outro_16_9.mp4</code>.
            </p>

            {/* Notification messages */}
            {outroUploadSuccess && (
              <div style={{ color: '#86efac', fontSize: '0.76rem', background: 'rgba(34, 197, 94, 0.15)', padding: '6px 10px', borderRadius: '6px', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
                ✓ {outroUploadSuccess}
              </div>
            )}
            {outroUploadError && (
              <div style={{ color: '#fca5a5', fontSize: '0.76rem', background: 'rgba(239, 68, 68, 0.15)', padding: '6px 10px', borderRadius: '6px', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                ⚠️ {outroUploadError}
              </div>
            )}

            {/* Live Video Preview Box */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
              <div
                style={{
                  width: '200px',
                  height: '112px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: '#090812',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                }}
              >
                <video
                  key={outroTimestamp}
                  src={`/api/media/assets/soulsync_outro_16_9.mp4?t=${outroTimestamp}`}
                  controls
                  playsInline
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </div>

              <div style={{ flex: 1, minWidth: '220px', fontSize: '0.72rem', color: '#94a3b8', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div><strong>Key:</strong> assets/soulsync_outro_16_9.mp4</div>
                <div><strong>Standard Duration:</strong> ~3.0 seconds (30fps / 90 frames)</div>
                <div><strong>Resolution:</strong> 1080p (1920x1080) MP4 (H.264 / AAC)</div>
                <div style={{ color: '#c084fc', marginTop: '2px' }}>Includes embedded logo animation + audio sting</div>
              </div>
            </div>
          </div>

          {/* Prefix bar */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '1rem',
            flexWrap: 'wrap'
          }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Prefix:</span>
            {['clips/', 'stems/', 'takes/', 'assets/', 'exports/', ''].map((pfx) => (
              <button
                key={pfx}
                type="button"
                onClick={() => setR2Prefix(pfx)}
                style={{
                  padding: '5px 12px',
                  borderRadius: 'var(--radius-full)',
                  border: r2Prefix === pfx ? '1px solid var(--accent-purple)' : '1px solid var(--border-glass)',
                  background: r2Prefix === pfx ? 'rgba(139, 92, 246, 0.2)' : 'var(--bg-glass)',
                  color: r2Prefix === pfx ? '#fff' : 'var(--text-muted)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.78rem',
                  cursor: 'pointer',
                  fontWeight: r2Prefix === pfx ? 700 : 400
                }}
              >
                {pfx ? `/${pfx}` : 'Root (all)'}
              </button>
            ))}

            <button
              type="button"
              onClick={() => loadR2(r2Prefix)}
              disabled={isLoadingR2}
              style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                padding: '5px 10px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border-glass)',
                background: 'transparent',
                color: 'var(--text-secondary)',
                fontSize: '0.78rem',
                cursor: 'pointer'
              }}
            >
              <RefreshCw size={12} className={isLoadingR2 ? 'spin' : ''} />
              <span>Refresh</span>
            </button>
          </div>

          {/* R2 Objects Table */}
          <div style={{
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-glass)',
            background: 'var(--bg-card)',
            overflow: 'hidden'
          }}>
            {isLoadingR2 ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                <p>Listing R2 objects...</p>
              </div>
            ) : !r2Data?.objects || r2Data.objects.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                <p>No objects found with prefix "{r2Prefix}".</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto', maxHeight: '68vh' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ background: 'rgba(0,0,0,0.3)', position: 'sticky', top: 0, zIndex: 1 }}>
                      <th style={thStyle}>Object Key</th>
                      <th style={thStyle}>Size</th>
                      <th style={thStyle}>Uploaded</th>
                      <th style={thStyle}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r2Data.objects.map((obj, idx) => (
                      <tr key={obj.key} style={{ borderTop: idx > 0 ? '1px solid var(--border-glass)' : 'none' }}>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <File size={14} color="var(--accent-cyan)" />
                            <code style={{ fontFamily: 'var(--font-mono)', color: '#fff', fontSize: '0.78rem' }}>
                              {obj.key}
                            </code>
                          </div>
                        </td>
                        <td style={{ ...tdStyle, color: 'var(--accent-emerald)', fontFamily: 'var(--font-mono)' }}>
                          {formatBytes(obj.size)}
                        </td>
                        <td style={{ ...tdStyle, color: 'var(--text-muted)', fontSize: '0.74rem' }}>
                          {obj.uploaded ? new Date(obj.uploaded).toLocaleString() : '—'}
                        </td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <button
                              type="button"
                              onClick={() => handleCopy(obj.key)}
                              title="Copy key"
                              style={{
                                padding: '4px 8px',
                                borderRadius: 'var(--radius-sm)',
                                border: '1px solid var(--border-glass)',
                                background: 'transparent',
                                color: copiedKey === obj.key ? 'var(--accent-emerald)' : 'var(--text-muted)',
                                cursor: 'pointer',
                                fontSize: '0.72rem'
                              }}
                            >
                              {copiedKey === obj.key ? <Check size={12} /> : <Copy size={12} />}
                            </button>
                            <a
                              href={`/api/media/${obj.key}`}
                              target="_blank"
                              rel="noreferrer"
                              title="Direct preview"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                                padding: '4px 8px',
                                borderRadius: 'var(--radius-sm)',
                                border: '1px solid var(--border-glass)',
                                background: 'transparent',
                                color: 'var(--accent-purple)',
                                textDecoration: 'none',
                                fontSize: '0.72rem'
                              }}
                            >
                              <ExternalLink size={12} />
                              <span>View</span>
                            </a>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────── */}
      {/* 2. KV NAMESPACE EXPLORER                       */}
      {/* ────────────────────────────────────────────── */}
      {activeSubTab === 'kv' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          {/* Key list */}
          <div style={{
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-glass)',
            background: 'var(--bg-card)',
            overflow: 'hidden'
          }}>
            <div style={{ padding: '10px 14px', background: 'rgba(0,0,0,0.25)', borderBottom: '1px solid var(--border-glass)', fontWeight: 700, fontSize: '0.85rem' }}>
              Namespace Keys ({kvData?.keys?.length || 0})
            </div>

            {isLoadingKv ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading keys...</div>
            ) : !kvData?.keys || kvData.keys.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No keys found in namespace.</div>
            ) : (
              <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                {kvData.keys.map(k => (
                  <div
                    key={k.name}
                    onClick={() => handleInspectKvKey(k.name)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 14px',
                      borderBottom: '1px solid var(--border-glass)',
                      cursor: 'pointer',
                      background: selectedKvKey === k.name ? 'rgba(139, 92, 246, 0.18)' : 'transparent'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Key size={14} color="var(--accent-purple)" />
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: '#fff' }}>{k.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Key value inspector */}
          <div style={{
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-glass)',
            background: 'var(--bg-card)',
            padding: '1.25rem',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#fff', marginBottom: '0.75rem' }}>
              {selectedKvKey ? `Key: ${selectedKvKey}` : 'Select a key to inspect value'}
            </h3>

            {isLoadingKvVal ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Loading value...</div>
            ) : selectedKvKey ? (
              <pre style={{
                flex: 1,
                padding: '1rem',
                borderRadius: 'var(--radius-md)',
                background: 'var(--bg-cosmic)',
                border: '1px solid var(--border-glass)',
                color: '#fff',
                fontFamily: 'var(--font-mono)',
                fontSize: '0.76rem',
                overflow: 'auto',
                lineHeight: 1.45,
                maxHeight: '55vh'
              }}>
                {typeof kvValueData === 'object' ? JSON.stringify(kvValueData, null, 2) : String(kvValueData)}
              </pre>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                Click on any KV key on the left to read its stored data.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle: React.CSSProperties = {
  padding: '8px 12px',
  textAlign: 'left',
  color: 'var(--text-muted)',
  fontFamily: 'var(--font-mono)',
  fontSize: '0.74rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

const tdStyle: React.CSSProperties = {
  padding: '8px 12px'
};
